import React, { useState, useMemo, useEffect } from 'react';
import styled from 'styled-components';
import { media } from '../theme/tokens';
import { KpiStrip } from '../components/KpiStrip';
import { SelectDropdown } from '../components/SelectDropdown';
import type { SelectOption } from '../components/SelectDropdown';
import { FilterChip } from '../components/FilterChip';
import { PageHeader } from '../components/PageHeader';
import { EmptyState } from '../components/EmptyState';
import { PeriodToggle } from '../components/PeriodToggle';
import {
  useNotebooks,
  useCountries,
  useMarkets,
  useNetworks,
  NotebookFilter,
  ProductType
} from 'apiTyped/hooks/notebooks';

// --- Constants ---

const START_YEAR = 2023;

// --- Documents (Market Notebooks) Page ---

export const Documents: React.FC = () => {
  const [filterValue, setFilterValue] = useState<NotebookFilter>({
    network: undefined,
    country: undefined,
    market: undefined,
    year: undefined,
    subscribed: false,
    productType: ProductType.Wine
  });

  const { countriesResponse } = useCountries();
  const { marketResponse } = useMarkets({
    countryId: filterValue.country?.value
  });
  const { networksResponse } = useNetworks();
  const { notebooksResponse, isLoadingNotebooks } = useNotebooks(filterValue);

  // Set default network to ON-Trade when data arrives
  useEffect(() => {
    if (networksResponse && filterValue.network === undefined) {
      const defaultNetwork = networksResponse.find((n) => n.code === 'ON');
      if (defaultNetwork) {
        setFilterValue((prev) => ({
          ...prev,
          network: {
            label: defaultNetwork.code === 'ON' ? 'On-Trade' : 'Off-Trade',
            value: defaultNetwork.id
          }
        }));
      }
    }
  }, [networksResponse]);

  // Sorted notebooks: continuous mode first
  const sortedNotebooks = useMemo(() => {
    if (!notebooksResponse) return [];
    return [...notebooksResponse].sort((a, b) => {
      if (a.continuous_mode && !b.continuous_mode) return -1;
      if (!a.continuous_mode && b.continuous_mode) return 1;
      return 0;
    });
  }, [notebooksResponse]);

  // Filter options
  const networkOptions: SelectOption[] = useMemo(() => {
    if (!networksResponse) return [];
    return networksResponse
      .filter((n) => ['ON', 'OFF'].includes(n.code))
      .map((n) => ({
        value: n.id,
        label: n.code === 'ON' ? 'On-Trade' : 'Off-Trade'
      }));
  }, [networksResponse]);

  const countryOptions: SelectOption[] = useMemo(() => {
    if (!countriesResponse) return [];
    return [...countriesResponse]
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((c) => ({ value: c.id, label: c.name }));
  }, [countriesResponse]);

  const marketOptions: SelectOption[] = useMemo(() => {
    if (!marketResponse) return [];
    return [...marketResponse]
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((m) => ({ value: m.id, label: m.name }));
  }, [marketResponse]);

  const yearOptions: SelectOption[] = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: currentYear - START_YEAR + 1 }, (_, i) => ({
      value: START_YEAR + i,
      label: String(START_YEAR + i)
    }));
  }, []);

  const subscribedCount = sortedNotebooks.filter((n) => n.subscribed).length;
  const totalCount = sortedNotebooks.length;

  return (
    <Page>
      <PageHeader
        title="Market Notebooks"
        subtitle="Browse and download your market study documents"
      />

      {/* KPI Strip */}
      <KpiStrip
        items={[
          { label: 'Total notebooks', value: totalCount },
          { label: 'Subscribed', value: subscribedCount },
          { label: 'Network', value: filterValue.network?.label || '—' },
          {
            label: 'Product',
            value:
              filterValue.productType === ProductType.Wine ? 'Wine' : 'Spirit'
          }
        ]}
      />

      {/* Filters */}
      <FiltersRow>
        {/* Product type toggle */}
        <PeriodToggle
          options={[
            { label: 'Wine', value: ProductType.Wine },
            { label: 'Spirit', value: ProductType.Spirit }
          ]}
          value={filterValue.productType}
          onChange={(val) =>
            setFilterValue((prev) => ({
              ...prev,
              productType: val as ProductType
            }))
          }
        />

        {/* Network */}
        <FilterItem>
          <FilterLabel>Network</FilterLabel>
          <SelectDropdown
            options={networkOptions}
            value={filterValue.network?.value ?? ''}
            onChange={(val) => {
              const opt = networkOptions.find((o) => String(o.value) === val);
              if (opt) {
                setFilterValue((prev) => ({
                  ...prev,
                  network: { label: opt.label, value: Number(opt.value) }
                }));
              }
            }}
            placeholder="All networks"
          />
        </FilterItem>

        {/* Country */}
        <FilterItem>
          <FilterLabel>Country</FilterLabel>
          <SelectDropdown
            options={countryOptions}
            value={filterValue.country?.value ?? ''}
            onChange={(val) => {
              const opt = countryOptions.find((o) => String(o.value) === val);
              if (opt) {
                setFilterValue((prev) => ({
                  ...prev,
                  country: { label: opt.label, value: Number(opt.value) },
                  market: null
                }));
              } else {
                setFilterValue((prev) => ({
                  ...prev,
                  country: null,
                  market: null
                }));
              }
            }}
            placeholder="All countries"
          />
        </FilterItem>

        {/* Market */}
        <FilterItem>
          <FilterLabel>Market</FilterLabel>
          <SelectDropdown
            options={marketOptions}
            value={filterValue.market?.value ?? ''}
            onChange={(val) => {
              const opt = marketOptions.find((o) => String(o.value) === val);
              if (opt) {
                setFilterValue((prev) => ({
                  ...prev,
                  market: { label: opt.label, value: Number(opt.value) }
                }));
              } else {
                setFilterValue((prev) => ({ ...prev, market: null }));
              }
            }}
            placeholder="All markets"
          />
        </FilterItem>

        {/* Year */}
        <FilterItem>
          <FilterLabel>Year</FilterLabel>
          <SelectDropdown
            options={yearOptions}
            value={filterValue.year?.value ?? ''}
            onChange={(val) => {
              const opt = yearOptions.find((o) => String(o.value) === val);
              if (opt) {
                setFilterValue((prev) => ({
                  ...prev,
                  year: { label: opt.label, value: Number(opt.value) }
                }));
              } else {
                setFilterValue((prev) => ({ ...prev, year: null }));
              }
            }}
            placeholder="All years"
          />
        </FilterItem>

        {/* Subscribed only toggle */}
        <FilterChip
          label="Filter"
          value={filterValue.subscribed ? 'Subscribed' : 'All'}
          active={!!filterValue.subscribed}
          onClick={() =>
            setFilterValue((prev) => ({
              ...prev,
              subscribed: !prev.subscribed
            }))
          }
        />
      </FiltersRow>

      {/* Notebook Grid */}
      {isLoadingNotebooks ? (
        <LoadingState>Loading notebooks…</LoadingState>
      ) : sortedNotebooks.length === 0 ? (
        <EmptyState message="No notebooks match your current filters." />
      ) : (
        <NotebookGrid>
          {sortedNotebooks.map((notebook, idx) => (
            <NotebookCard key={idx} $locked={!notebook.subscribed}>
              {/* Cover image */}
              <NotebookCover>
                {notebook.market_image_path ? (
                  <CoverImage
                    src={notebook.market_image_path}
                    alt={notebook.market_name}
                  />
                ) : (
                  <CoverPlaceholder>{notebook.market_name[0]}</CoverPlaceholder>
                )}
                {notebook.continuous_mode && (
                  <ContinuousBadge>Continuous</ContinuousBadge>
                )}
                {!notebook.subscribed && <LockedOverlay>🔒</LockedOverlay>}
              </NotebookCover>

              {/* Content */}
              <NotebookBody>
                <NotebookName>{notebook.market_name}</NotebookName>
                <NotebookMeta>
                  <MetaItem>
                    {notebook.network_code === 'ON' ? 'On-Trade' : 'Off-Trade'}
                  </MetaItem>
                  <MetaDot />
                  <MetaItem>{notebook.release_date}</MetaItem>
                </NotebookMeta>
                {notebook.subscribed &&
                  notebook.dn_client > 0 &&
                  notebook.market_presentation_path && (
                    <DownloadLink
                      href={notebook.market_presentation_path}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Download presentation
                    </DownloadLink>
                  )}
                {!notebook.subscribed && (
                  <LockedLabel>Not subscribed</LockedLabel>
                )}
              </NotebookBody>
            </NotebookCard>
          ))}
        </NotebookGrid>
      )}
    </Page>
  );
};

// --- Styled Components ---

const Page = styled.div`
  max-width: 1400px;
`;

const FiltersRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 12px;
  margin: 20px 0;
  padding: 16px 20px;
  background: var(--white);
  border: 1px solid var(--line);
  border-radius: var(--radius-md);
`;

const FilterItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 140px;
`;

const FilterLabel = styled.span`
  font-size: 10.5px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--ink-faint);
  font-weight: 600;
`;

const NotebookGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;

  ${media.sm} {
    grid-template-columns: repeat(2, 1fr);
  }

  ${media.md} {
    grid-template-columns: repeat(3, 1fr);
  }

  ${media.lg} {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const NotebookCard = styled.div<{ $locked: boolean }>`
  display: flex;
  flex-direction: column;
  background: var(--white);
  border: 1px solid var(--line);
  border-radius: var(--radius-md);
  overflow: hidden;
  transition:
    box-shadow 0.15s,
    transform 0.15s;
  opacity: ${({ $locked }) => ($locked ? 0.7 : 1)};

  &:hover {
    box-shadow: var(--shadow-md);
    transform: translateY(-2px);
  }
`;

const NotebookCover = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 4 / 3;
  background: var(--paper-warm);
  overflow: hidden;
`;

const CoverImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const CoverPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: var(--font-serif);
  font-size: 48px;
  color: var(--ink-faint);
  background: var(--paper-warm);
`;

const ContinuousBadge = styled.span`
  position: absolute;
  top: 10px;
  left: 10px;
  padding: 3px 10px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  background: var(--olive);
  color: var(--white);
  border-radius: var(--radius-sm);
`;

const LockedOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(43, 61, 84, 0.25);
  font-size: 32px;
`;

const NotebookBody = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const NotebookName = styled.h3`
  font-family: var(--font-sans-semibold, var(--font-sans));
  font-size: 14px;
  font-weight: 600;
  color: var(--ink);
  margin: 0;
  line-height: 1.3;
`;

const NotebookMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: var(--ink-faint);
`;

const MetaItem = styled.span``;

const MetaDot = styled.span`
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: var(--ink-faint);
`;

const DownloadLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  padding: 7px 14px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--white);
  background: var(--terracotta);
  border-radius: var(--radius-sm);
  text-decoration: none;
  transition: background 0.15s;

  &:hover {
    background: var(--terracotta-dark);
  }
`;

const LockedLabel = styled.span`
  margin-top: 4px;
  font-size: 11px;
  color: var(--ink-faint);
  font-style: italic;
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 60px 20px;
  font-size: 14px;
  color: var(--ink-muted);
`;
